import React, { useState, useEffect, useCallback } from 'react';
import { documentAPI, authToken } from '../../services/api';
import api from '../../services/api';
import { useGroup } from '../../contexts/GroupContext';
import { formatDate, formatTimestamp, getStatusBadgeClass, getStatusColor } from '../../utils/helpers';
import { SUCCESS_MESSAGES, ERROR_MESSAGES, DOCUMENT_STATUSES } from '../../utils/constants';
import DocumentForm from './DocumentForm';
import LoadingSpinner from '../Common/LoadingSpinner';
import ErrorMessage from '../Common/ErrorMessage';
import SuccessMessage from '../Common/SuccessMessage';

const DocumentList = () => {
  const { selectedGroupId } = useGroup();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      let response;
      const trimmedQuery = filterText.trim();
      if (trimmedQuery) {
        console.log('Fetching documents by search:', trimmedQuery, 'status:', statusFilter || 'none', 'page:', page, 'pageSize:', pageSize);
        response = await documentAPI.search(trimmedQuery, {
          status: statusFilter || undefined,
          page,
          pageSize
        });
        console.log('Search response:', response.data);
      } else if (statusFilter && statusFilter.trim()) {
        console.log('Fetching documents by status:', statusFilter, 'page:', page, 'pageSize:', pageSize);
        response = await documentAPI.getByStatus(statusFilter, page, pageSize);
        console.log('Status filter response:', response.data);
      } else {
        // Use regular getAll API if no status filter
        console.log('Fetching all documents, page:', page, 'pageSize:', pageSize);
        response = await documentAPI.getAll(page, pageSize, selectedGroupId);
        console.log('Get all response:', response.data);
      }
      
      setDocuments(response.data.data || []);
      setTotal(response.data.pagination?.total || response.data.count || 0);
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || ERROR_MESSAGES.NETWORK_ERROR;
      setError(errorMessage);
      console.error('Error fetching documents:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        url: err.config?.url
      });
    } finally {
      setLoading(false);
    }
  }, [filterText, statusFilter, page, pageSize, selectedGroupId]);

  // Handle status filter change
  const handleStatusFilterChange = async (e) => {
    const newStatus = e.target.value;
    console.log('Status filter changed to:', newStatus || 'All Statuses');
    setStatusFilter(newStatus);
    setPage(1); // Reset to first page when status filter changes
  };

  // Delete document
  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      await documentAPI.delete(docId);
      setSuccess(SUCCESS_MESSAGES.DOCUMENT_DELETED);
      fetchDocuments();
    } catch (err) {
      setError(ERROR_MESSAGES.NETWORK_ERROR);
      console.error('Error deleting document:', err);
    }
  };

  // Update status
  const handleStatusUpdate = async (docId, newStatus) => {
    try {
      await documentAPI.updateStatus(docId, newStatus);
      setSuccess(SUCCESS_MESSAGES.STATUS_UPDATED);
      fetchDocuments();
    } catch (err) {
      setError(ERROR_MESSAGES.NETWORK_ERROR);
      console.error('Error updating status:', err);
    }
  };

  // Handle form submission
  const handleFormSubmit = () => {
    setShowForm(false);
    setEditingDocument(null);
    setSuccess(SUCCESS_MESSAGES.DOCUMENT_CREATED);
    fetchDocuments();
  };

  // Handle edit
  const handleEdit = (document) => {
    setEditingDocument(document);
    setShowForm(true);
  };

  // Handle create new
  const handleCreateNew = () => {
    setEditingDocument(null);
    setShowForm(true);
  };

  // Handle view PDF
  const handleViewPdf = async (docId) => {
    try {
      // Use axios with blob response type to include authentication
      const response = await api.get(`/documents/${docId}/pdf`, {
        responseType: 'blob',
      });

      // response.data is already a Blob when responseType is 'blob'
      const blobUrl = window.URL.createObjectURL(response.data);
      
      // Open in new window
      const newWindow = window.open(blobUrl, '_blank');
      if (!newWindow) {
        setError('Please allow pop-ups to view PDF files.');
        return;
      }
      
      // Clean up blob URL after a delay (browser will clean up when tab closes)
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('Error viewing PDF:', error);
      // Try to parse error message from blob response if available
      let errorMessage = 'Failed to open PDF file. Please try again.';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          errorMessage = json.error || errorMessage;
        } catch (_) {
          // If parsing fails, use default message
        }
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      setError(errorMessage);
    }
  };

  // Handle view XML
  const handleViewXml = async (docId) => {
    try {
      // Use axios with blob response type to include authentication
      const response = await api.get(`/documents/${docId}/xml`, {
        responseType: 'blob',
      });

      // response.data is already a Blob when responseType is 'blob'
      const blobUrl = window.URL.createObjectURL(response.data);
      
      // Open in new window
      const newWindow = window.open(blobUrl, '_blank');
      if (!newWindow) {
        setError('Please allow pop-ups to view XML files.');
        return;
      }
      
      // Clean up blob URL after a delay (browser will clean up when tab closes)
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('Error viewing XML:', error);
      // Try to parse error message from blob response if available
      let errorMessage = 'Failed to open XML file. Please try again.';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          errorMessage = json.error || errorMessage;
        } catch (_) {
          // If parsing fails, use default message
        }
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      setError(errorMessage);
    }
  };

  // Filter documents (only text search, status is handled by API)
  useEffect(() => {
    const handler = setTimeout(() => {
    fetchDocuments();
    }, 300);
    return () => clearTimeout(handler);
  }, [fetchDocuments]);

  if (loading && documents.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="document-list">
      <div className="page-header">
        <h2>Document Management</h2>
        <button className="btn btn-primary" onClick={handleCreateNew}>
          <i class="fa-solid fa-plus"></i> Add New Document
        </button>
      </div>

      {error && <ErrorMessage message={error} onClose={() => setError(null)} />}
      {success && <SuccessMessage message={success} onClose={() => setSuccess(null)} />}

      <div className="filters">
        <div className="search-bar">
           <div style={{ flex: 1, position: 'relative' }}>
          <span style={{ 
            position: 'absolute', 
            left: '12px', 
            top: '50%', 
            transform: 'translateY(-50%)',
            color: '#0F2854',
            fontSize: '1rem'
          }}><i className="fa-solid fa-magnifying-glass"></i></span>
          <input
            type="text"
            placeholder="Search documents..."
            value={filterText}
            onChange={(e) => {
              setFilterText(e.target.value);
              setPage(1);
            }}
            className="form-control"
            style={{ 
              width: '100%',
              paddingLeft: '40px',
              borderRadius: '0.5rem',
              border: '1px solid #e2e8f0',
              fontSize: '0.875rem',
              transition: 'all 0.2s ease'
            }}
          />
         </div> 
        </div>
        <div className="status-filter">
          <select
            value={statusFilter}
            onChange={handleStatusFilterChange}
            className="form-control"
              style={{ 
              borderRadius: '0.5rem',
              border: '1px solid #e2e8f0',
              fontSize: '0.875rem',
              transition: 'all 0.2s ease'
            }}
          >
            <option value="">All Statuses</option>
            <option value={DOCUMENT_STATUSES.ACTIVE}>Active</option>
            <option value={DOCUMENT_STATUSES.REVOKED}>Revoked</option>
            <option value={DOCUMENT_STATUSES.EXPIRED}>Expired</option>
          </select>
        </div>
      </div>

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Document ID</th>
              <th>Type</th>
              <th>Employee</th>
              <th>Employee ID</th>
              <th>Issue Date</th>
              <th>Expiry Date</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {documents.map((document) => (
              <tr key={document.id}>
                <td>{document.doc_id}</td>
                <td>{document.doc_type}</td>
                <td>{document.employee?.full_name}</td>
                <td>{document.employee?.employee_id}</td>
                <td>{formatDate(document.issue_date)}</td>
                <td>{formatDate(document.expiry_date)}</td>
                <td>
                  <span 
                    className={`badge ${getStatusBadgeClass(document.status)}`}
                    style={{ backgroundColor: getStatusColor(document.status) }}
                  >
                    {document.status}
                  </span>
                  
                </td>
                <td>{formatTimestamp(document.created_at)}</td>
                <td>
                  <div className="action-buttons" style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {document.pdf_path && (
                    <button
                      className="btn btn-sm btn-outline-info"
                      onClick={() => handleViewPdf(document.doc_id)}
                      title="View PDF"
                    >
                      <i class="fa-solid fa-file-lines"></i> PDF
                    </button>
                    )}
                    {document.xml_metadata_path && (
                    <button
                      className="btn btn-sm btn-outline-info"
                      onClick={() => handleViewXml(document.doc_id)}
                      title="View XML"
                    >
                      <i class="fa-solid fa-file-code"></i> XML
                    </button>
                    )}
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => handleEdit(document)}
                      title="Edit"
                    >
                        <i class="fa-solid fa-pen-to-square"></i>
                    </button>
                    {document.status === DOCUMENT_STATUSES.ACTIVE && (
                      <button
                        className="btn btn-sm btn-outline-warning"
                        onClick={() => handleStatusUpdate(document.doc_id, DOCUMENT_STATUSES.REVOKED)}
                        title="Revoke Document"
                      >
                      <i class="fa-solid fa-ban"></i>
                      </button>
                    )}
                    {document.status === DOCUMENT_STATUSES.REVOKED && (
                      <button
                        className="btn btn-sm btn-outline-success"
                        onClick={() => handleStatusUpdate(document.doc_id, DOCUMENT_STATUSES.ACTIVE)}
                        title="Activate Document"
                      >
                      <i class="fa-solid fa-file-circle-check"></i>
                      </button>
                    )}
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(document.doc_id)}
                      title="Delete"
                    >
                       <i class="fa-solid fa-trash-can"></i>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
        <div>
          <button className="btn btn-outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</button>
          <span style={{ margin: '0 8px' }}>Page {page}</span>
          <button className="btn btn-outline" onClick={() => setPage(p => (p * pageSize < total ? p + 1 : p))} disabled={page * pageSize >= total}>Next</button>
        </div>
        <div>
          <label style={{ marginRight: 8 }}>Rows per page</label>
          <select value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(1); }}>
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
          <span style={{ marginLeft: 12 }}>{Math.min((page - 1) * pageSize + 1, total)}–{Math.min(page * pageSize, total)} of {total}</span>
        </div>
      </div>

      {documents.length === 0 && !loading && (
        <div className="no-data">
          <p>No documents found.</p>
        </div>
      )}

      {showForm && (
        <DocumentForm
          document={editingDocument}
          onClose={() => {
            setShowForm(false);
            setEditingDocument(null);
          }}
          onSubmit={handleFormSubmit}
        />
      )}
    </div>
  );
};

export default DocumentList;
